import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, useTheme, API } from "../App";
import axios from "axios";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import {
  LayoutDashboard,
  Upload,
  History,
  LogOut,
  User,
  Menu,
  X,
  FileText,
  Moon,
  Sun,
  Settings,
  Shield,
  BarChart3,
  ShieldCheck,
  UserPlus,
  MessageSquare,
  Users,
  Sparkles,
  BadgeCheck,
  Crown,
  Inbox,
  BookOpen,
  Calendar,
  Database,
  FileCheck,
  Send,
  GraduationCap,
  Video,
  Globe,
  Terminal,
  Palette,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Bell,
  Search,
  Lock,
  Mic,
  Building,
  UserCheck,
  ClipboardCheck,
  Zap,
  Newspaper,
  Gavel,
  FileSpreadsheet,
  FileSignature,
  FileCode,
  Smartphone,
  Target,
  Gauge,
  CalendarClock,
  ListChecks,
  Receipt,
  Plug,
  Package,
  UsersRound,
  DollarSign,
  Building2,
  Scale,
  Activity,
  FileSearch,
  Network,
  KeyRound,
  Loader2,
  ShieldAlert } from "lucide-react";
import MobileNav from "./MobileNav";
import { VideoTourModal } from "./VideoTourModal";
import { DemoTour, HeaderTourButton } from "./DemoTour";
import { OfflineIndicator } from "./OfflineIndicator";
import LanguageSelector from "./LanguageSelector";
import JobStatusBadge from "./notifications/JobStatusBadge";
import { useJobNotifications } from "../hooks/useJobNotifications";
import DemoBanner from "./DemoBanner";
import AiPausedBanner from "./AiPausedBanner";
import ClinicalReviewBanner from "./ClinicalReviewBanner";
import { useModules } from "../hooks/useModules";
import ModuleLockedPrompt from "./ModuleLockedPrompt";
import AlacarteLockedUpsell from "./AlacarteLockedUpsell";
import { buildNavSections, buildClinicianSections, dailyToolsFor, buildDailySections } from "../config/navModel";
import { NavToolsToggle } from "./layout/NavToolsToggle";
import CommandPalette from "./CommandPalette";

const ALL_TOOLS_KEY = "hg_nav_all_tools";

const Layout = () => {
  const { user, logout, isAdmin, isSuperAdmin, isPhysician, isNetworkAdmin, token } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  // Per-tenant module gating: dims locked nav items + gates locked pages.
  // Key on user id (present after cookie-auth hydration), NOT the in-memory token.
  const { locked: lockedModules, lockedModuleForPath, trialForPath } = useModules(user?.id);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [companyBranding, setCompanyBranding] = useState(null);
  // À-la-carte entitlements. Hydrate from a per-session cache so a hard reload /
  // deep link does NOT flash locked-page content before /alacarte/my resolves.
  const [alacarte, setAlacarte] = useState(() => {
    if (typeof window === "undefined") return { alacarte: false, enabled: {}, catalog: [] };
    try {
      const cached = sessionStorage.getItem("__hg_alacarte");
      if (cached) return JSON.parse(cached);
    } catch (e) { /* ignore */ }
    return { alacarte: false, enabled: {}, catalog: [] };
  });
  const [alacarteLoaded, setAlacarteLoaded] = useState(() => {
    if (typeof window === "undefined") return false;
    return !!sessionStorage.getItem("__hg_alacarte");
  });
  const [showVideoTour, setShowVideoTour] = useState(false);
  const [showAllTools, setShowAllTools] = useState(() => {
    try { return localStorage.getItem(ALL_TOOLS_KEY) === "1"; } catch (e) { return false; }
  });
  // 2026-02-25 sprint #5 — unread compliance-alert count for the sidebar badge.
  // Admins only. Polled every 2 minutes; endpoint is cheap (one Mongo
  // count_documents + a small find with limit=50).
  // 2026-02-25 sprint #5 — unread compliance-alert count for the sidebar badge.
  // Admins only. Polled every 2 minutes; endpoint is cheap (one Mongo
  // count_documents + a small find with limit=50).
  //
  // Persist across remounts via sessionStorage. ProtectedRoute unmounts
  // Layout whenever the auth-loading flag flips (which happens on any
  // /auth/me refresh) — without the cache, the badge blinks to 0 for
  // ~200ms every 5 minutes. The sessionStorage cache smooths that out.
  const [unreadComplianceAlerts, setUnreadComplianceAlerts] = useState(() => {
    if (typeof window === "undefined") return 0;
    const cached = sessionStorage.getItem("__hg_unread_compliance_alerts");
    const n = Number.parseInt(cached ?? "0", 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  });
  const updateUnread = (n) => {
    setUnreadComplianceAlerts(n);
    try {
      sessionStorage.setItem("__hg_unread_compliance_alerts", String(n));
    } catch (e) {
      // sessionStorage disabled — silently degrade.
    }
  };

  // Job notifications hook
  const { 
    activeJobs, 
    activeJobCount, 
    permissionGranted, 
    trackJob, 
    cancelTracking, 
    requestPermission 
  } = useJobNotifications(token);

  // Expose trackJob globally for other components
  useEffect(() => {
    window.trackAsyncJob = trackJob;
    return () => {
      delete window.trackAsyncJob;
    };
  }, [trackJob]);

  // Fetch à-la-carte module entitlements (gates purchased features).
  // Key on user id (cookie-auth hydration) — the in-memory token is null after a reload.
  useEffect(() => {
    if (!user?.id) return;
    axios
      .get(`${API}/alacarte/my`)
      .then((r) => {
        setAlacarte(r.data);
        try { sessionStorage.setItem("__hg_alacarte", JSON.stringify(r.data)); } catch (e) { /* ignore */ }
      })
      .catch(() => { /* fail-safe: keep any cached entitlements */ })
      .finally(() => setAlacarteLoaded(true));
  }, [user?.id]);

  // Fetch company branding for logged-in users.
  // Key on user id (cookie-auth hydration) — the in-memory token is null after a reload.
  useEffect(() => {
    const fetchCompanyBranding = async () => {
      if (user?.id && user?.company_id) {
        try {
          const response = await axios.get(`${API}/company/current`);
          if (response.data && response.data.id !== "default") {
            setCompanyBranding(response.data);
          }
        } catch (error) {
          console.error("Failed to fetch company branding:", error);
        }
      }
    };
    fetchCompanyBranding();
  }, [user?.id, user?.company_id]);

  // Poll the unread compliance-alert count so the sidebar badge stays
  // fresh without a manual refresh. Admins/super-admins only.
  useEffect(() => {
    if (!user?.id || !(isAdmin || isSuperAdmin)) return;

    const fetchUnread = async () => {
      try {
        const res = await axios.get(
          `${API}/admin/compliance-alerts/recent?days=30`,
          { withCredentials: true, headers: { "Cache-Control": "no-cache" } },
        );
        updateUnread(res.data?.count ?? 0);
      } catch (err) {
        // 401/403/500 all resolve to "no badge" — don't spam the console.
        updateUnread(0);
      }
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 120_000); // every 2 minutes
    return () => clearInterval(iv);
  }, [user?.id, isAdmin, isSuperAdmin]);

  // Dynamic branding values
  const brandName = companyBranding?.branding?.company_name || "HospiceGuard";
  const primaryColor = companyBranding?.branding?.primary_color || "#10b981";

  const toggleDarkMode = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const handleLogout = () => {
    try {
      sessionStorage.removeItem("__hg_alacarte");
      sessionStorage.removeItem("__hg_unread_compliance_alerts");
    } catch (e) { /* ignore */ }
    logout();
    navigate("/login");
  };

  // Staff role only has access to scribe feature
  const isStaff = user?.role === "staff";
  const staffDiscipline = user?.discipline;
  
  // Check if user can access documentation tools (CTI/F2F)
  // Only physicians, providers (staff with clinical discipline), and admin can access
  const canAccessDocumentationTools = isAdmin || isPhysician || (isStaff && staffDiscipline);

  // Build navigation from the single role/entitlement-aware model (navModel.js).
  const navSections = buildNavSections({
    role: user?.role,
    isAdmin,
    isSuperAdmin,
    isPhysician,
    isNetworkAdmin,
    isStaff,
    canAccessDocumentationTools,
    unreadComplianceAlerts,
  });

  // ── À-la-carte gating: hide the 5 clinician tools an à-la-carte agency
  // hasn't purchased (full-platform agencies are unaffected). ────────────────
  const blockedPaths = new Set();
  if (alacarte?.alacarte) {
    (alacarte.catalog || []).forEach((m) => {
      if (alacarte.enabled && alacarte.enabled[m.id] === false) {
        (m.paths || []).forEach((p) => blockedPaths.add(p));
      }
    });
  }
  // À-la-carte free trials: {path: days_left} for features on an active trial.
  const alacarteTrialPaths = {};
  if (alacarte?.alacarte && alacarte?.trials) {
    (alacarte.catalog || []).forEach((m) => {
      const d = alacarte.trials[m.id];
      if (d) (m.paths || []).forEach((p) => { alacarteTrialPaths[p] = d; });
    });
  }
  const navSectionsFiltered = navSections
    .map((s) => ({ ...s, items: (s.items || []).filter((i) => !blockedPaths.has(i.path)) }))
    .filter((s) => (s.items || []).length > 0);

  // À-la-carte route-level gate: a deep link to a locked feature lands on the
  // upsell screen instead of the page. Matches exact path or nested sub-paths.
  const alacarteLockedForPath = (pathname) => {
    if (!alacarte?.alacarte) return null;
    for (const m of alacarte.catalog || []) {
      if (alacarte.enabled && alacarte.enabled[m.id] === false) {
        const hit = (m.paths || []).some(
          (p) => pathname === p || pathname.startsWith(p + "/"),
        );
        if (hit) return m;
      }
    }
    return null;
  };

  // Clinician role: lean sidebar of ONLY the purchased à-la-carte tools.
  const isClinician = user?.role === "clinician";
  const clinicianSections = buildClinicianSections(alacarte);

  // Clinicians and physicians get a short daily menu; "Show all tools" expands to everything their role can open.
  const dailyPaths = isClinician ? null : dailyToolsFor({ isAdmin, isNetworkAdmin, isPhysician, isStaff, canAccessDocumentationTools });
  const shortNav = !!dailyPaths && !showAllTools;
  const allNavSections = isClinician ? clinicianSections : navSectionsFiltered;
  const dailySections = dailyPaths ? buildDailySections(navSectionsFiltered, dailyPaths) : null;
  const effectiveNavSections = shortNav ? dailySections : allNavSections;
  const hiddenToolCount = dailySections
    ? allNavSections.reduce((n, s) => n + s.items.filter((i) => i.path !== "/app/settings").length, 0)
      - dailySections[0].items.length
    : 0;
  const toggleAllTools = () => {
    const next = !showAllTools;
    setShowAllTools(next);
    try { localStorage.setItem(ALL_TOOLS_KEY, next ? "1" : "0"); } catch (e) { /* ignore */ }
  };

  // Compute the active gate once per render (reused by header title + main gate).
  const currentAlacarteLocked = alacarteLockedForPath(location.pathname);
  const currentNetworkLocked = lockedModuleForPath(location.pathname);
  // While entitlements are still loading (first login, no cache), hold gated
  // paths behind a loader so locked content never flashes.
  const gatingPending = !!user?.id && !alacarteLoaded;

  const isActive = (path) => location.pathname === path;
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "");

  return (
    <DemoTour>
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col">
      <DemoBanner />
      <AiPausedBanner />
      <CommandPalette sections={allNavSections} />
      <ClinicalReviewBanner variant="global" />
      <div className="flex-1 flex">
      {/* Defense Sidebar - Desktop */}
      <aside 
        className={`hidden lg:flex flex-col fixed left-0 top-0 h-full z-50 transition-all duration-300 ${
          sidebarCollapsed ? "w-20" : "w-72"
        }`}
        style={{
          background: "linear-gradient(180deg, #020617 0%, #0f172a 40%, #0c1929 100%)"
        }}
        data-testid="sidebar"
      >
        {/* Gradient glow effect at top */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[300px] h-[200px] rounded-full blur-[100px] opacity-20" style={{ backgroundColor: primaryColor }} />
        </div>
        
        {/* Sidebar Header */}
        <div className="relative flex items-center justify-between px-5 py-6 border-b border-slate-800/50">
          <Link to="/app/my-day" className="flex items-center gap-4" data-testid="logo-link">
            <div 
              className="w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg shadow-lg relative overflow-hidden"
              style={{ 
                backgroundColor: primaryColor,
                color: "#ffffff",
                boxShadow: `0 0 30px -5px ${primaryColor}80`
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
              {companyBranding?.branding?.logo_url ? (
                <img 
                  src={companyBranding.branding.logo_url} 
                  alt={brandName} 
                  className="w-full h-full object-contain rounded-2xl relative z-10" 
                />
              ) : (
                <Shield className="w-6 h-6 relative z-10" />
              )}
            </div>
            {!sidebarCollapsed && (
              <div className="overflow-hidden">
                <h1 className="text-xl font-bold text-white tracking-tight font-manrope">{brandName}</h1>
                <p className="text-xs text-slate-400 truncate">
                  {companyBranding?.branding?.tagline || "Defense System"}
                </p>
              </div>
            )}
          </Link>
        </div>

        {/* Navigation Sections */}
        <nav className="flex-1 overflow-y-auto py-5 px-3 space-y-7 scrollbar-thin scrollbar-thumb-slate-700">
          {effectiveNavSections
            .map((s) => ({ ...s, items: s.items.filter((i) => i.path !== "/app/settings") }))
            .filter((s) => s.items.length > 0)
            .map((section, idx) => (
            <div key={idx}>
              {!sidebarCollapsed && (
                <p className="px-4 mb-3 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                  {section.title}
                </p>
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const itemLocked = !!lockedModuleForPath(item.path);
                  const itemTrial = itemLocked ? null : trialForPath(item.path);
                  const itemAlaTrial = itemLocked ? null : alacarteTrialPaths[item.path];
                  return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 group relative overflow-hidden ${
                      isActive(item.path)
                        ? "text-white"
                        : itemLocked
                        ? "text-slate-500 hover:text-slate-300"
                        : "text-slate-400 hover:text-white"
                    }`}
                    style={isActive(item.path) ? {
                      background: `linear-gradient(90deg, ${primaryColor}30 0%, ${primaryColor}10 100%)`,
                      borderLeft: `3px solid ${primaryColor}`,
                      boxShadow: `inset 0 0 20px -10px ${primaryColor}40`
                    } : {}}
                    title={sidebarCollapsed ? item.label : (itemLocked ? `${item.label} — module not enabled` : undefined)}
                  >
                    {/* Hover effect */}
                    {!isActive(item.path) && (
                      <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/0 to-cyan-600/0 group-hover:from-emerald-600/10 group-hover:to-cyan-600/5 rounded-xl transition-all duration-300" />
                    )}
                    <item.icon className={`w-5 h-5 flex-shrink-0 relative z-10 ${isActive(item.path) ? "text-emerald-400" : "group-hover:text-emerald-400 transition-colors duration-300"} ${itemLocked ? "opacity-50" : ""}`} />
                    {!sidebarCollapsed && <span className={`relative z-10 ${itemLocked ? "opacity-60" : ""}`}>{item.label}</span>}
                    {!sidebarCollapsed && itemLocked && (
                      <Lock className="w-3.5 h-3.5 ml-auto relative z-10 text-slate-500" data-testid={`nav-lock-${item.path}`} />
                    )}
                    {!sidebarCollapsed && !itemLocked && itemTrial && (
                      <Badge
                        className="ml-auto h-5 px-1.5 text-[10px] text-white relative z-10 bg-amber-500 hover:bg-amber-500"
                        data-testid={`nav-trial-${item.path}`}
                        title={`Trial · ${itemTrial.days_left} day${itemTrial.days_left === 1 ? "" : "s"} left`}
                      >
                        TRIAL {itemTrial.days_left}d
                      </Badge>
                    )}
                    {!sidebarCollapsed && !itemLocked && !itemTrial && itemAlaTrial && (
                      <Badge
                        className="ml-auto h-5 px-1.5 text-[10px] text-white relative z-10 bg-amber-500 hover:bg-amber-500"
                        data-testid={`nav-ala-trial-${item.path}`}
                        title={`Free trial · ${itemAlaTrial} day${itemAlaTrial === 1 ? "" : "s"} left`}
                      >
                        TRIAL {itemAlaTrial}d
                      </Badge>
                    )}
                    {!sidebarCollapsed && !itemLocked && !itemTrial && !itemAlaTrial && item.badge && (
                      <Badge
                        className={
                          "ml-auto h-5 px-1.5 text-[10px] text-white relative z-10 " +
                          (item.badgeClass ||
                            "bg-emerald-500 hover:bg-emerald-500")
                        }
                        data-testid={item.badgeTestId}
                      >
                        {item.badge}
                      </Badge>
                    )}
                  </Link>
                  );
                })}
              </div>
            </div>
          ))}
          {dailyPaths && (
            <NavToolsToggle showAll={showAllTools} hiddenCount={hiddenToolCount} onToggle={toggleAllTools} collapsed={sidebarCollapsed} />
          )}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-slate-800/50">
          <Link
            to="/app/settings"
            data-testid="nav-settings"
            className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 ${
              isActive("/app/settings") 
                ? "bg-emerald-600/20 text-emerald-400" 
                : "text-slate-400 hover:text-white hover:bg-slate-800/50"
            }`}
          >
            <Settings className="w-5 h-5" />
            {!sidebarCollapsed && <span>Settings</span>}
          </Link>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="flex items-center justify-center w-full mt-3 py-2.5 rounded-xl text-slate-500 hover:text-white hover:bg-slate-800/50 transition-all duration-300 border border-slate-800/50 hover:border-slate-700"
          >
            {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col transition-all duration-300 max-w-full overflow-x-hidden ${sidebarCollapsed ? "lg:ml-20" : "lg:ml-72"}`}>
        {/* Top Header Bar - Futuristic glass effect */}
        <header className="sticky top-0 z-40 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-b border-slate-200/80 dark:border-slate-800/50 shadow-sm">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            {/* Left side - Mobile menu & Search */}
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>

              {/* Mobile Logo */}
              <Link to="/app/my-day" className="flex items-center gap-2 lg:hidden">
                <div 
                  className="w-9 h-9 rounded-xl flex items-center justify-center shadow-lg"
                  style={{ 
                    backgroundColor: primaryColor,
                    boxShadow: `0 0 20px -5px ${primaryColor}60`
                  }}
                >
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-slate-900 dark:text-white font-manrope">{brandName}</span>
              </Link>

              {/* Page Title - Desktop */}
              <div className="hidden lg:block">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white font-manrope tracking-tight">
                  {currentAlacarteLocked?.label
                    || currentNetworkLocked?.name
                    || currentNetworkLocked?.label
                    || allNavSections.flatMap(s => s.items).find(i => isActive(i.path))?.label
                    || (/^\/app\/(audit|result)\//.test(location.pathname) ? "Audit Result"
                      : ({ "/app/ambient-scribe": "Ambient Scribe", "/app/emr-automation": "EMR Automation" }[location.pathname] || "Dashboard"))}
                </h2>
              </div>

              {/* Quick-jump command palette trigger */}
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event("open-command-palette"))}
                data-testid="command-palette-trigger"
                aria-label="Open quick-jump palette"
                className="flex items-center gap-2 ml-2 px-2.5 lg:px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
              >
                <Search className="w-4 h-4" />
                <span className="hidden lg:inline text-sm">Jump to…</span>
                <kbd className="hidden lg:inline ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">{isMac ? "⌘K" : "Ctrl K"}</kbd>
              </button>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-3">
              {/* 2FA Badge */}
              {user?.two_factor_enabled && (
                <Badge className="hidden sm:flex gap-1.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 px-3 py-1 rounded-full font-bold text-xs shadow-glow-sm">
                  <Lock className="w-3 h-3" />
                  Secured
                </Badge>
              )}

              {/* Language Selector */}
              <LanguageSelector />

              {/* Job Status Badge - Async Processing Notifications */}
              <JobStatusBadge 
                activeJobs={activeJobs}
                onCancelJob={cancelTracking}
                permissionGranted={permissionGranted}
                onRequestPermission={requestPermission}
              />

              {/* Dark mode toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleDarkMode}
                className="rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200"
                data-testid="dark-mode-toggle"
              >
                {theme === "dark" ? <Sun className="w-5 h-5 text-amber-500" /> : <Moon className="w-5 h-5 text-slate-600" />}
              </Button>

              {/* Guided Tour Button */}
              <HeaderTourButton />

              {/* Video Tour Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowVideoTour(true)}
                className="rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-200"
                data-testid="video-tour-btn"
                title="Watch Training Videos"
              >
                <Video className="w-5 h-5" />
              </Button>

              {/* Offline Indicator */}
              <OfflineIndicator />

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-full pl-2 pr-3 gap-2" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 rounded-full bg-slate-900 dark:bg-emerald-600 flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div className="hidden sm:block text-left">
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{user?.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{isAdmin ? "Admin" : "User"}</p>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/app/settings")} data-testid="settings-button">
                    <Settings className="w-4 h-4 mr-2" />
                    {t("nav.settings")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-button">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t("auth.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setMobileMenuOpen(false)}>
            <div 
              className="absolute left-0 top-0 h-full w-72 bg-slate-900 p-4 overflow-y-auto"
              style={{ background: "linear-gradient(180deg, #0f172a 0%, #1e293b 100%)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-lg font-bold text-white">{brandName}</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)} className="text-white">
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {effectiveNavSections.map((section, idx) => (
                <div key={idx} className="mb-6">
                  <p className="px-3 mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                    {section.title}
                  </p>
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const itemLocked = !!lockedModuleForPath(item.path);
                      return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                          isActive(item.path)
                            ? "bg-emerald-600/20 text-emerald-400"
                            : itemLocked
                            ? "text-slate-500"
                            : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                        }`}
                      >
                        <item.icon className={`w-5 h-5 ${itemLocked ? "opacity-50" : ""}`} />
                        <span className={itemLocked ? "opacity-60" : ""}>{item.label}</span>
                        {itemLocked ? (
                          <Lock className="w-3.5 h-3.5 ml-auto text-slate-500" />
                        ) : item.badge && (
                          <Badge
                            className={
                              "ml-auto h-5 px-1.5 text-[10px] text-white " +
                              (item.badgeClass ||
                                "bg-emerald-500 hover:bg-emerald-500")
                            }
                          >
                            {item.badge}
                          </Badge>
                        )}
                      </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
              {dailyPaths && (
                <NavToolsToggle showAll={showAllTools} hiddenCount={hiddenToolCount} onToggle={toggleAllTools} testId="mobile-nav-show-all-tools" />
              )}
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-6 pb-24 lg:pb-6 overflow-x-hidden">
          <div className="max-w-7xl mx-auto w-full">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                {gatingPending ? (
                  <div className="flex items-center justify-center py-32" data-testid="entitlements-loading">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                  </div>
                ) : currentNetworkLocked ? (
                  <ModuleLockedPrompt module={currentNetworkLocked} />
                ) : currentAlacarteLocked ? (
                  <AlacarteLockedUpsell
                    module={currentAlacarteLocked}
                    price={alacarte?.prices?.[currentAlacarteLocked.id]}
                    canTrial={!(alacarte?.trials_used || []).includes(currentAlacarteLocked.id)}
                  />
                ) : (
                  <Outlet />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </main>

        {/* Footer - Desktop */}
        <footer className="hidden lg:block border-t border-slate-200 dark:border-slate-800 py-4 px-6">
          <div className="flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
            <p>© 2026 {brandName} - HIPAA Compliant Defense System</p>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Shield className="w-4 h-4 text-emerald-500" />
                Protected
              </span>
              <span className="flex items-center gap-1">
                <Lock className="w-4 h-4 text-emerald-500" />
                Encrypted
              </span>
            </div>
          </div>
        </footer>
      </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileNav />

      {/* Video Tour Modal */}
      <VideoTourModal open={showVideoTour} onOpenChange={setShowVideoTour} />
    </div>
    </DemoTour>
  );
};

export default Layout;
